import * as React from 'react';
import {useState} from 'react';
import {InfoBox} from '@flexe/ui-components';
import ItemMasterService from '../../shared/services/ItemMasterService';
import {ItemMasterDetailHeader} from './ItemMasterDetailHeader';
import {ItemMasterDetailProps, ToastProps, UpdatedItemDetails} from './ItemMasterDetail';
import {InventoryInfo, InventoryProperty} from './ItemMasterInterfaces';
import UomTileEdit from './UomTileEdit';
import SkuDetailsEdit from './SkuDetailsEdit';
import {fetchSkuDetailProperties, mapToItemBulkUpsertPayload, mapToUpdatedItemDetails} from './ItemMasterDetailMapper';

export interface ItemMasterEditProps extends ItemMasterDetailProps {
  cancelEdit: () => void;
  onSaved: (updatedItemDetails: UpdatedItemDetails) => void;
}

export interface SkuDetailProperties {
  isHazmat: boolean;
  limitedQuantity: boolean;
  unNumber: string;
  pkgCode: string;
  lotTrackingEnabled: boolean;
  expiryDateTrackingEnabled: boolean;
  bagAndTapeRequired: boolean;
}

export type Packaging = 'each' | 'carton' | 'inner_pack' | 'pallet';

export const PACKAGING_ORDER: Record<Packaging, number> = {
  each: 3,
  inner_pack: 2,
  carton: 1,
  pallet: 0
};

export const ItemMasterEdit: React.FC<ItemMasterEditProps> = (props) => {
  const itemMasterService = new ItemMasterService();
  const [inventoryInfo, setInventoryInfo] = useState<InventoryInfo>(props.inventoryInfo);
  const [skuDetailProperties, setSkuDetailProperties] = useState<SkuDetailProperties>(fetchSkuDetailProperties(props));
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastProps>({visible: false, type: 'error', message: ''});

  const disableSkuEdit = !props.allowSkuEdit || (!props.isShipper && !props.inventory_control_user);

  const updateSkuDetailProperties = (patch: Partial<SkuDetailProperties>) => {
    setSkuDetailProperties((prev) => ({...prev, ...patch}));
  };

  const updateInventoryInfo = (patch: Partial<InventoryInfo>) => {
    setInventoryInfo((prev) => ({...prev, ...patch}));
  };

  const updateInventoryProp = (patch: Partial<InventoryProperty>) => {
    setInventoryInfo((prev) => {
      const properties = [...prev.properties];
      const idx = properties.findIndex((p) => p.packaging === patch.packaging);
      if (idx === -1) return prev;
      properties[idx] = {...properties[idx], ...patch};
      return {...prev, properties};
    });
  };

  const isUnchanged = () => {
    return (
      JSON.stringify(inventoryInfo) === JSON.stringify(props.inventoryInfo) &&
      JSON.stringify(skuDetailProperties) === JSON.stringify(fetchSkuDetailProperties(props))
    );
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isUnchanged()) {
      setToast({visible: true, type: 'info', message: 'No changes detected'});
      return;
    }

    setSubmitting(true);
    try {
      const updatedItemDetails = mapToUpdatedItemDetails(
        inventoryInfo,
        props.inventory_configuration,
        skuDetailProperties
      );
      const payload = mapToItemBulkUpsertPayload(props.shipperCompanyId, inventoryInfo, skuDetailProperties);

      const res = await itemMasterService.bulkUpsertItemMaster(payload);

      if (res.isSuccess) {
        props.onSaved(updatedItemDetails);
      } else {
        setToast({
          visible: true,
          type: 'error',
          message:
            `Item Master Detail Update Failed: ` +
            (res?.errors?.map((err: any) => err.detail).join('; ') || 'Unknown Error')
        });
      }
    } catch (err) {
      setToast({visible: true, type: 'error', message: err?.message || 'Update failed'});
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="item-master-detail">
      {toast.visible && (
        <div>
          <InfoBox info={toast.message} infoType={toast.type} />
        </div>
      )}
      <form id="item-master-edit-form" className="item-master-edit container-fluid" onSubmit={onSubmit}>
        <div className="container-fluid detail-header">
          <ItemMasterDetailHeader isShipper={props.isShipper} />
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 form-group form-group-sku">
                  <label className="form-label">SKU</label>
                  <input
                    className="form-control"
                    value={inventoryInfo.sku}
                    disabled={disableSkuEdit}
                    onChange={(e) => updateInventoryInfo({sku: e.target.value})}
                  />
                </div>
                <div className="col-sm-8 text-right">
                  <button type="button" className="btn-cancel" onClick={props.cancelEdit}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} form="item-master-edit-form">
                    {submitting ? 'Saving…' : 'Confirm Changes'}
                  </button>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 form-group form-group-description">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={inventoryInfo.description}
                    disabled={!(props.isShipper || props.inventory_control_user)}
                    onChange={(e) => updateInventoryInfo({description: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            {inventoryInfo.properties
              .sort((a, b) => {
                const oa = PACKAGING_ORDER[a.packaging as Packaging];
                const ob = PACKAGING_ORDER[b.packaging as Packaging];
                return oa - ob;
              })
              .map((property, i) => (
                <UomTileEdit
                  key={i}
                  inventoryControlUser={props.inventory_control_user}
                  isShipper={props.isShipper}
                  faiReceiveWorkflow={props.fai_receive_workflow}
                  prevReviewDate={props.inventoryInfo.review_date}
                  inventoryProp={property}
                  inventoryInfo={inventoryInfo}
                  onChangeProp={updateInventoryProp}
                  onChangeInfo={updateInventoryInfo}
                />
              ))}
          </div>
          <div className="col-lg-6">
            <SkuDetailsEdit
              onChangeInfo={updateInventoryInfo}
              onChangeSkuDetailProperties={updateSkuDetailProperties}
              {...props}
              skuDetailProperties={skuDetailProperties}
            />
          </div>
        </div>
      </form>
    </div>
  );
};
